/**
 * `sgScopedDeck(groupId, deckId)` — the per-deck-within-a-group facade
 * returned by `sync.studyGroups.group(g).decks.deck(id)`.
 *
 * Surface is intentionally read-heavy: `useCards` / `getCards` /
 * `useProperties` / `getProperties` for reactive + snapshot reads of
 * the SG-side projection; `properties.update` and `permissions.*` for
 * the small set of shared-scope mutations that route through
 * `updateDeckProperty` in `helpers.ts` (which emits the framework's
 * per-row `sg-deckProperties-push`). Card mutations live on the
 * user-side scoped deck (`sync.decks.deck(mirrorDeckId).cards.*`);
 * writing directly to the SG side would skip the field-scope router
 * and lose the editor's personal-scope state.
 */

import { useShallow } from 'zustand/react/shallow';

import { useStudyGroupStore } from '../internal/store/study-group-store';
import { removeGroupDeck } from './deck-removal';
import { currentUserId, updateDeckProperty } from './helpers';
import type { SGScopedDeck } from './types';

export function sgScopedDeck(groupId: string, deckId: string): SGScopedDeck {
  return {
    id: deckId,
    useCards: () =>
      useStudyGroupStore(
        useShallow((s) => {
          const rec = s.groups[groupId]?.cards[deckId] ?? {};
          return Object.values(rec);
        }),
      ),
    getCards: () => {
      const rec = useStudyGroupStore.getState().groups[groupId]?.cards[deckId] ?? {};
      return Object.values(rec);
    },
    useProperties: () => useStudyGroupStore((s) => s.groups[groupId]?.deckProps[deckId]),
    getProperties: () => useStudyGroupStore.getState().groups[groupId]?.deckProps[deckId],
    properties: {
      update: (patch) => {
        updateDeckProperty(groupId, deckId, patch);
      },
    },
    permissions: {
      use: () => {
        // The returned object is re-derived on every store update; the
        // outer hook compares props shallowly so setMode/addEditor
        // trigger a re-render for any consumer that subscribes.
        const slice = useStudyGroupStore((s) => s.groups[groupId]?.deckProps[deckId]);
        return {
          editPermission: slice?.editPermission ?? 'owner-only',
          editors: slice?.editors ?? [],
        };
      },
      setMode: (mode) => {
        updateDeckProperty(groupId, deckId, { editPermission: mode });
      },
      setEditors: (userIds) => {
        const current = useStudyGroupStore.getState().groups[groupId]?.deckProps[deckId];
        if (!current) return;
        // Dedupe + drop the deck owner (they're implicitly always
        // allowed). Keeps the stored list tight and avoids the
        // confusing "owner listed alongside editors" UI state.
        const cleaned = Array.from(
          new Set(userIds.filter((id) => id !== current.addedByUserId)),
        );
        updateDeckProperty(groupId, deckId, { editors: cleaned });
      },
      addEditor: (userId) => {
        const current = useStudyGroupStore.getState().groups[groupId]?.deckProps[deckId];
        if (!current) return;
        if (userId === current.addedByUserId) return;
        if ((current.editors ?? []).includes(userId)) return;
        const next = [...(current.editors ?? []), userId];
        sgScopedDeck(groupId, deckId).permissions.setEditors(next);
      },
      removeEditor: (userId) => {
        const current = useStudyGroupStore.getState().groups[groupId]?.deckProps[deckId];
        if (!current) return;
        const next = (current.editors ?? []).filter((id) => id !== userId);
        sgScopedDeck(groupId, deckId).permissions.setEditors(next);
      },
    },
    // Optimistic, settled by the group — see `deck-removal.ts`: a refusal
    // puts the deck back, an unanswered removal is re-sent on the next sync,
    // and only an accepted one reaches the group feed.
    remove: () => removeGroupDeck(groupId, deckId, currentUserId()),
  };
}
